'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useUser } from '@clerk/nextjs';

interface HostingContextValue {
  isHosting: boolean;
  setIsHosting: (_isHosting: boolean) => void;
}

const HostingContext = createContext<HostingContextValue | undefined>(undefined);

export function HostingProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user } = useUser();

  // Try to determine initial role state from multiple sources:
  // 1. Check if we're on a host-related path
  // 2. Check user metadata for saved preference
  // 3. Fall back to localStorage
  const getInitialHostingState = (): boolean => {
    if (pathname && pathname.includes('/host')) {
      return true;
    } else if (user && user.publicMetadata?.role) {
      return user.publicMetadata.role === 'host';
    } else if (typeof window !== 'undefined') {
      return window.localStorage.getItem('isHosting') === 'true';
    }
    return false;
  };

  const [isHosting, setIsHosting] = useState<boolean>(getInitialHostingState);

  // Update localStorage when role changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('isHosting', Boolean(isHosting).toString());
    }
  }, [isHosting]);

  return (
    <HostingContext.Provider value={{ isHosting, setIsHosting }}>
      {children}
    </HostingContext.Provider>
  );
}

export default function useHosting() {
  const ctx = useContext(HostingContext);
  if (!ctx) throw new Error('useHosting must be used within <HostingProvider>');
  return ctx;
}
